import React, { useState, useEffect, useCallback } from 'react';
import { USER_ICON } from "assets/constants/Icons";
import { PHONE_NUMBER_PATTERN, emailPattern, TYPE_SUCCESS, TYPE_FAIL, INTERVAL } from "assets/constants/Constants";
import Spinner from 'components/Spinner';
import ShowToast from 'components/ShowToast';
import Select from 'react-select';
import { toCapitaliseWord } from 'lib/utils';
import { getRoleListCall, createUserCall, UpdateUserCall, getUserList } from "lib/axiosUtils";

const CreateUserPage = (props) => {
    const editData = props.history.location?.state;
    const [isLoading, setIsLoading] = useState(true);
    const [userId] = useState(editData ? editData._id : null);
    const [firstName, setFirstName] = useState(editData ? editData.first_name : "");
    const [lastName, setLastName] = useState(editData ? editData.last_name : "");
    const [emailId, setEmailId] = useState(editData ? editData.email_id : "");
    const [phoneNumber, setPhoneNumber] = useState(editData ? editData.phone_number : "");
    const userType = useState(editData?.userType || "");
    const [userRole, setUserRole] = useState(editData ? editData.role : "");
    const [isFromProfile] = useState(editData ? editData.isFromProfile : "");
    const [selectedCity, setSelectedCity] = useState(editData?.city_id ? { value: editData.city_id, label: editData.city_id } : []);
    const [selectedRegion, setSelectedRegion] = useState(editData?.region_id ? { value: editData.region_id, label: editData.region_id } : []);
    const [roleList, setRoleList] = useState([]);
    const [userList, setUserList] = useState([]);
    const [error, setError] = useState({});
    const [roleListComponent, setRoleListComponent] = useState(
        "<option> Select User Role </option>"
    );
    const [regionListComponent, setRegionListComponent] = useState(
        "<option> Select User Region </option>"
    );
    const [cityListComponent, setCityListComponent] = useState(
        "<option> Select User Location </option>"
    );
    const [referredCode, setReferredCode] = useState(editData ? editData.referred_code : "");
    const [referralCode] = useState(editData ? editData.referral_code : "");
    const [referredCodeMatch, setReferredCodeMatch] = useState(false);

    const getMetadata = async () => {
        const storedMetadata = localStorage.getItem("metadata");
        if (storedMetadata) {
            console.log("metadata from localStorage::", storedMetadata);
            return JSON.parse(storedMetadata);
        } else {
            console.warn("No metadata found in localStorage");
            return null;
        }
    };

    const renderOptionsRegion = useCallback((optionArray) => {
        if (!optionArray || !Array.isArray(optionArray)) return [];
        const options = optionArray.map((item) => ({
            label: item.region,
            value: item.region_id,
        }));

        const matchedRegion = options.find(opt => opt.value === selectedRegion?.value);
        if (matchedRegion && selectedRegion?.label !== matchedRegion.label) {
            setSelectedRegion(matchedRegion);
        }
        return options;
    }, [selectedRegion]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const metadata = await getMetadata();
                let regionOption = [];
                if (metadata && metadata[0]?.meta_data) {
                    regionOption = renderOptionsRegion(metadata[0].meta_data.regions);
                }
                setRegionListComponent(regionOption);

            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
            }
        };

        fetchData();
    }, [renderOptionsRegion]);

    const renderOptionsCity = useCallback((optionArray) => {
        if (!optionArray || !Array.isArray(optionArray)) return [];

        const options = optionArray.map(item => ({
            label: item.city_name,
            value: item.city_id,
        }));

        const matchedCity = options.find(opt => opt.value === selectedCity?.value);
        if (matchedCity && selectedCity?.label !== matchedCity.label) {
            setSelectedCity(matchedCity);
        }

        return options;
    }, [selectedCity]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const metadata = await getMetadata();
                let cityOption = [];
                if (metadata && metadata[0]?.meta_data) {
                    cityOption = renderOptionsCity(metadata[0].meta_data.cities);
                }

                setCityListComponent(cityOption);
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
            }
        };

        fetchData();
    }, [renderOptionsCity]);

    const setUserRoleComponent = useCallback(async () => {
        if (!roleList || roleList.length === 0) return;

        const listComponent = roleList.map(item => ({
            label: toCapitaliseWord(item.user_role),
            value: item._id
        }));

        if (userRole) {
            const matchedRole = listComponent.find(
                role => role.value === userRole
            );

            if (matchedRole) {
                setUserRole(matchedRole);
            }
        }

        setRoleListComponent(listComponent);
    }, [roleList, userRole]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [roleData, userListData] = await Promise.all([
                    getRoleListCall({}, {}),
                    getUserList({}, {})
                ]);

                setRoleList(roleData.data.result);
                setUserList(userListData.data.result);
                setIsLoading(false);
                setUserRoleComponent();

            } catch (error) {
                console.error("Error in componentDidMount logic:", error);
                setIsLoading(false);
            }
        };

        fetchData();
    }, [setUserRoleComponent]);

    const firstNameChangeHandler = (event) => {
        setFirstName(event.target.value);
    };

    const lastNameChangeHandler = (event) => {
        setLastName(event.target.value);
    }

    const cityChangeHandler = (event) => {
        setSelectedCity({
            label: event.label,
            value: event.value
        });
    };

    const emailIdChangeHandler = (event) => {
        setEmailId(event.target.value);
    }

    const phoneNumberChangeHandler = (event) => {
        setPhoneNumber(event.target.value);
    }

    const referredCodeMatchHandler = async (event) => {
        if (event.target.value) {
            if (userId) {
                const referralCodeFound = userList.find((findItem) => findItem.referral_code?.trim() === event.target.value.trim())
                if (!referralCodeFound) {
                    ShowToast('Referred Code is not correct, please try again!', TYPE_FAIL, INTERVAL, "", "");
                    setReferredCodeMatch(false);
                }
                else {
                    setReferredCodeMatch(true);
                }
            }
        }
    }

    const emailUniqueHandler = (event) => {
        if (event.target.value) {
            if (!userId) {
                const userFound = userList.find((findItem) => findItem.email_id.trim().toLowerCase() === event.target.value.trim().toLowerCase())
                if (userFound) {
                    ShowToast('Email Id already present, please try with different email id!', TYPE_FAIL, INTERVAL, "", "");
                    setEmailId("");
                }
            }
        }
    }

    const userRoleChaneHandler = (event) => {
        setUserRole(event);
    }

    const userRegionChangeHandler = (event) => {
        setSelectedRegion({
            label: event.label,
            value: event.value
        });
    };

    const referredCodeChangeHandler = (event) => {
        setReferredCode(event.target.value);
    }

    const generateReferralCode = (username) => {
        if (!username) return "";
        const prefix = username.slice(0, 3).toUpperCase(); // first 3 chars
        const randomPart = Math.random().toString(36).substring(2, 7).toUpperCase(); // random 5 chars
        return `${prefix}-${randomPart}`;
    };

    const isFormValid = () => {
        let isValid = true;
        const error = {};

        if (!firstName) {
            isValid = false;
            error.firstName = "Please enter first name";
        }

        if (!lastName) {
            isValid = false;
            error.lastName = "Please Enter last name";
        }

        if (!selectedCity || !selectedCity.value) {
            isValid = false;
            error.location = "Please Enter Location";
        }

        if (!emailId) {
            isValid = false;
            error.emailId = "Please enter email id";
        }

        if (emailId) {
            if (!emailPattern.test(emailId)) {
                isValid = false;
                error.emailId = "Please enter valid email address.";
            } else {
                if (!userId) {
                    const findUser = userList.find(
                        item =>
                            item.email_id?.trim().toLowerCase() ===
                            emailId.trim().toLowerCase()
                    );

                    if (findUser) {
                        isValid = false;
                        error.emailId =
                            "Email Id already present, please try with different email id";
                    }
                }
            }
        }

        if (!phoneNumber) {
            isValid = false;
            error.phoneNumber = "Please enter phone number";
        }

        if (phoneNumber && phoneNumber !== "undefined") {
            if (!PHONE_NUMBER_PATTERN.test(phoneNumber)) {
                isValid = false;
                error.phoneNumber = "Please enter valid phone number.";
            }
        }

        if (!userRole || !userRole.value) {
            isValid = false;
            error.userRole = "Please select user role";
        }

        if (!selectedRegion || !selectedRegion.value) {
            isValid = false;
            error.selectedRegion = "Please select user Region";
        }

        if (referredCode && !referredCodeMatch) {
            isValid = false;
            error.referredCode = "Please enter correct referred code";
        }

        setError(error);
        return isValid;
    };

    const createUserHandler = async (event) => {
        event.preventDefault();
        if (isFormValid()) {
            setIsLoading(true);

            const referralCode = generateReferralCode(emailId);
            const allowedCharater = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ!@#$%^&*()_+abcdefghijklmnopqrstuvwxyz';
            let otp = '';
            for (let i = 0; i < 8; i++) {
                const randomNumber = Math.floor(Math.random() * allowedCharater.length);
                otp += allowedCharater.substring(randomNumber, randomNumber + 1);
            }

            const requestData = {
                userFirstName: firstName,
                userLastName: lastName,
                userEmailId: emailId,
                userPhoneNumber: phoneNumber,
                userName: emailId,
                userCity: selectedCity?.value ? selectedCity.value : "",
                userType: userType,
                userRole: userRole && userRole.value ? userRole.value : "",
                userselectedRegion: selectedRegion?.value ? selectedRegion.value : "",
                userReferredCode: referredCode,
                userReferralCode: referralCode,
                userOtp: otp
            }

            let userResponse = null;
            if (userId === null) {
                userResponse = await createUserCall({}, requestData);
            }
            else {
                userResponse = await UpdateUserCall({}, requestData, userId);
            }

            if (userResponse.statusCode === 200) {
                if (userId === null) {
                    ShowToast('User created Successfully', TYPE_SUCCESS, INTERVAL, props, `${isFromProfile ? "project-list" : "/user-page"}`);
                }
                else {
                    ShowToast('User updated Successfully', TYPE_SUCCESS, INTERVAL, props, `${isFromProfile ? "project-list" : "/user-page"}`);
                }
                props.history.push(
                    `${isFromProfile ? "home-page" : "/user-page"}`
                );
            }
        }
    }

    const cancelHandler = () => {
        props.history.push(`${isFromProfile ? "home-page" : "/user-page"}`);
    }

    return (
        <>
            {
                isLoading ? (
                    <Spinner />
                ) : (
                    <div className="main-content">
                        <div className="back">
                            <ul className="breadcrumb1">
                                <li><a href="/user-page">User Master</a></li>
                                {
                                    userId === null ? (
                                        <li>Add New User</li>
                                    ) : (
                                        <li>Edit User</li>
                                    )
                                }
                            </ul>
                        </div>
                        <div className="add-user-wrapper">
                            <div className="mx-auto text-left border create-user-div">
                                <form onSubmit={createUserHandler} className="pt-1">
                                    <div className="user-icon-wrapper">
                                        <h4>{userId === null ? "Add New User" : "Edit User"}</h4>
                                        {referralCode && (
                                            <div className="referral-code-wrapper" style={{ display: "flex", alignItems: "center", gap: "8px" }} >
                                                <h6 style={{ margin: 0 }}>
                                                    Referral Code : {referralCode}
                                                </h6>
                                                <i className="fa fa-copy" title="Copy referral code" style={{ cursor: "pointer", color: "#000", fontWeight: "bold" }}
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(referralCode);
                                                        alert("Referral code copied!");
                                                    }}
                                                ></i>
                                            </div>
                                        )}
                                        <i className={`${USER_ICON} list-table-icon`}></i>
                                    </div>
                                    <div className="row mt-0">
                                        <div className="input-label-group col-md-6">
                                            <label className="form-label">First Name <span className="text-danger">*</span></label>
                                            <input className="form-control brand-font p-3 placeholderColor add-user-input add-user-input-height" value={firstName} onChange={firstNameChangeHandler} placeholder="Enter First Name" />
                                            <div className="text-danger error-msg">
                                                {error.firstName}
                                            </div>
                                        </div>
                                        <div className="input-label-group col-md-6">
                                            <label className="form-label">Last Name <span className="text-danger">*</span></label>
                                            <input className="form-control brand-font p-3 placeholderColor add-user-input add-user-input-height" value={lastName} onChange={lastNameChangeHandler} placeholder="Enter Last Name" />
                                            <div className="text-danger error-msg">
                                                {error.lastName}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mt-0">
                                        <div className="input-label-group col-md-6 email-input">
                                            <label className="form-label">Email Id <span className="text-danger">*</span></label>
                                            <input className="form-control brand-font p-3 placeholderColor add-user-input add-user-input-height lowercase-email" value={emailId} onChange={emailIdChangeHandler} placeholder="Enter Email Id" disabled={isFromProfile} onBlur={emailUniqueHandler} />
                                            <div className="text-danger error-msg">
                                                {error.emailId}
                                            </div>
                                        </div>
                                        <div className="input-label-group col-md-6">
                                            <label className="form-label">Phone Number <span className="text-danger">*</span></label>
                                            <input className="form-control brand-font p-3 placeholderColor add-user-input add-user-input-height" value={phoneNumber} onChange={phoneNumberChangeHandler} placeholder="Enter Phone Number" maxLength="10" />
                                            <div className="text-danger error-msg">
                                                {error.phoneNumber}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="input-label-group col-md-6">
                                            <label className="form-label" htmlFor="general-information-skills">User Region<span className="text-danger">*</span></label>
                                            <div className="form-wrap-inner">
                                                <Select
                                                    placeholder={`Select User Region`}
                                                    value={selectedRegion}
                                                    onChange={userRegionChangeHandler}
                                                    options={regionListComponent}
                                                    isSearchable={true}
                                                    isDisabled={isFromProfile}
                                                />
                                            </div>
                                            <div className="text-danger error-msg">
                                                {error.selectedRegion}
                                            </div>
                                        </div>
                                        <div className="input-label-group col-md-6">
                                            <label className="form-label" htmlFor="general-information-skills">User Location </label>
                                            <div className="form-wrap-inner">
                                                <Select
                                                    placeholder={`Select User Location`}
                                                    value={selectedCity}
                                                    onChange={cityChangeHandler}
                                                    options={cityListComponent}
                                                    isSearchable={true}
                                                    isDisabled={isFromProfile}
                                                />
                                            </div>
                                            <div className="text-danger error-msg">
                                                {error.selectedLocation}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mt-0">
                                        <div className="col-md-6">
                                            <div className="form-wrap">
                                                <label className="form-label" htmlFor="general-information-skills">User Role <span className="text-danger">*</span></label>
                                                <div className="form-wrap-inner">
                                                    <Select
                                                        placeholder={`Select User Role`}
                                                        value={userRole}
                                                        onChange={userRoleChaneHandler}
                                                        options={roleListComponent}
                                                        isSearchable={true}
                                                        isDisabled={isFromProfile}
                                                    />
                                                </div>
                                            </div>
                                            <div className="text-danger error-msg">
                                                {error.userRole}
                                            </div>
                                        </div>
                                        <div className="input-label-group col-md-6">
                                            <label className="form-label">Referred By Code </label>
                                            <input className="form-control brand-font p-3 placeholderColor add-user-input add-user-input-height" value={referredCode} onChange={referredCodeChangeHandler} onBlur={referredCodeMatchHandler} placeholder="Enter Referred Code" disabled={userId ? true : false} />
                                            <div className="text-danger error-msg">
                                                {error.referredCode}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mt-0 mt-4 mb-5 display-inline">
                                        <div className="form-button-div">
                                            <button className="brand-button" onClick={createUserHandler}>{userId ? "Update" : "Create"}</button>
                                            <button className="cancel_button ml-5" onClick={cancelHandler}>Cancel</button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                        <div className="row dummy_row"></div>
                    </div>
                )
            }
        </>
    );
}

export default CreateUserPage;